/**
 * The width bands in JS (the same four numbers as the comment table at the top of theme.css; CSS cannot share them, so these are the
 * only width literals either side may use). `useBand` answers the viewport; `BandProvider` overrides it for tests and previews.
 */
import { createContext, createElement, useContext, useSyncExternalStore, type ReactNode } from "react";

export const BAND_MAX = { phone: 480, tabletS: 768, tablet: 1024, laptop: 1359 } as const;
export type Band = "phone" | "tabletS" | "tablet" | "laptop" | "wide";

const ORDER: readonly Band[] = ["phone", "tabletS", "tablet", "laptop", "wide"];

/** Subscribe to one media query; without `matchMedia` (jsdom, SSR) the query never matches. */
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (notify) => {
      if (typeof window === "undefined" || typeof window.matchMedia !== "function") return () => undefined;
      const mql = window.matchMedia(query);
      mql.addEventListener("change", notify);
      return () => mql.removeEventListener("change", notify);
    },
    () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(query).matches,
    () => false,
  );
}

const BandContext = createContext<Band | null>(null);

/** Forces `useBand()` below it to `band` (tests, the preview). */
export function BandProvider({ band, children }: { readonly band: Band; readonly children: ReactNode }) {
  return createElement(BandContext.Provider, { value: band }, children);
}

/** The narrowest band the viewport falls in; "wide" when there is no matchMedia. */
export function useBand(): Band {
  const forced = useContext(BandContext);
  const phone = useMediaQuery(`(max-width: ${BAND_MAX.phone}px)`);
  const tabletS = useMediaQuery(`(max-width: ${BAND_MAX.tabletS}px)`);
  const tablet = useMediaQuery(`(max-width: ${BAND_MAX.tablet}px)`);
  const laptop = useMediaQuery(`(max-width: ${BAND_MAX.laptop}px)`);
  if (forced !== null) return forced;
  return phone ? "phone" : tabletS ? "tabletS" : tablet ? "tablet" : laptop ? "laptop" : "wide";
}

/** True at `phone` and `tabletS`: where overlays become sheets and the shell collapses. */
export function isMobileBand(band: Band): boolean {
  return ORDER.indexOf(band) <= ORDER.indexOf("tabletS");
}
